// src/components/Inspection/InspectionModal.jsx
import React from 'react';
import { X, CreditCard, CheckCircle, AlertCircle } from 'lucide-react';

const InspectionModal = ({ isOpen, onClose, inspection }) => {
  if (!isOpen || !inspection) return null;

  const getPaymentStatusColor = (status) => {
    const colors = {
      initialized: '#FFA500',
      pending: '#FF6B6B',
      success: '#4ECDC4',
      failed: '#E74C3C',
      refunded: '#9B59B6'
    };
    return colors[status] || '#999';
  };

  const getPaymentStatusLabel = (status) => {
    const labels = {
      initialized: 'Initializing Payment',
      pending: 'Payment Pending',
      success: 'Payment Successful',
      failed: 'Payment Failed',
      refunded: 'Refunded'
    };
    return labels[status] || 'Unknown';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getTimeSlot = (time) => {
    const slots = {
      morning: '9 AM - 12 PM',
      afternoon: '12 PM - 4 PM',
      evening: '4 PM - 7 PM'
    };
    return slots[time] || time;
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.title}>Inspection Details</h2>
          <button onClick={onClose} style={styles.closeButton}>
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {/* Car Information */}
          <section style={styles.section}>
            <h3 style={styles.sectionTitle}>🚗 Car Information</h3>
            <div style={styles.grid}>
              <div style={styles.gridItem}>
                <span style={styles.gridLabel}>Brand & Model</span>
                <span style={styles.gridValue}>
                  {inspection?.car_brand} {inspection?.model}
                </span>
              </div>
              <div style={styles.gridItem}>
                <span style={styles.gridLabel}>Year</span>
                <span style={styles.gridValue}>{inspection?.year}</span>
              </div>
              <div style={styles.gridItem}>
                <span style={styles.gridLabel}>Booking Reference</span>
                <span style={styles.gridValue}>{inspection?.booking_reference}</span>
              </div>
              <div style={styles.gridItem}>
                <span style={styles.gridLabel}>Status</span>
                <span style={{
                  ...styles.statusBadge,
                  backgroundColor: '#4ECDC4'
                }}>
                  {inspection?.status?.charAt(0).toUpperCase() + inspection?.status?.slice(1) || 'Confirmed'}
                </span>
              </div>
            </div>
          </section>

          {/* Inspection Details */}
          <section style={styles.section}>
            <h3 style={styles.sectionTitle}>📋 Inspection Details</h3>
            <div style={styles.grid}>
              <div style={styles.gridItem}>
                <span style={styles.gridLabel}>Inspection Type</span>
                <span style={styles.gridValue}>
                  {inspection?.inspection_type === 'home' ? 'Home Inspection' : 'Branch Inspection'}
                </span>
              </div>
              <div style={styles.gridItem}>
                <span style={styles.gridLabel}>City</span>
                <span style={styles.gridValue}>{inspection?.city}</span>
              </div>
              <div style={styles.gridItem}>
                <span style={styles.gridLabel}>Preferred Date</span>
                <span style={styles.gridValue}>{formatDate(inspection?.preferred_date)}</span>
              </div>
              <div style={styles.gridItem}>
                <span style={styles.gridLabel}>Preferred Time</span>
                <span style={styles.gridValue}>{getTimeSlot(inspection?.preferred_time)}</span>
              </div>
            </div>

            {inspection?.inspection_type === 'home' && inspection?.seller_address && (
              <div style={{ ...styles.gridItem, marginTop: '12px' }}>
                <span style={styles.gridLabel}>Address</span>
                <span style={styles.gridValue}>{inspection.seller_address}</span>
              </div>
            )}

            {inspection?.inspection_type === 'branch' && inspection?.branch_location && (
              <div style={{ ...styles.gridItem, marginTop: '12px' }}>
                <span style={styles.gridLabel}>Branch Location</span>
                <span style={styles.gridValue}>{inspection.branch_location}</span>
              </div>
            )}

            {inspection?.special_instructions && (
              <div style={{ ...styles.gridItem, marginTop: '12px' }}>
                <span style={styles.gridLabel}>Special Instructions</span>
                <span style={styles.gridValue}>{inspection.special_instructions}</span>
              </div>
            )}
          </section>

          {/* Contact Information */}
          <section style={styles.section}>
            <h3 style={styles.sectionTitle}>📱 Contact Information</h3>
            <div style={styles.grid}>
              <div style={styles.gridItem}>
                <span style={styles.gridLabel}>Contact Person</span>
                <span style={styles.gridValue}>{inspection?.contact_person}</span>
              </div>
              <div style={styles.gridItem}>
                <span style={styles.gridLabel}>Phone Number</span>
                <span style={styles.gridValue}>+91 {inspection?.contact_phone}</span>
              </div>
            </div>
          </section>

          {/* Payment Information Section */}
          <section style={styles.paymentSection}>
            <div style={styles.paymentSectionHeader}>
              <CreditCard size={22} color="#003566" />
              <h3 style={styles.sectionTitle}>💳 Payment Summary</h3>
            </div>

            <div style={styles.paymentBox}>
              {/* Amount Paid */}
              <div style={styles.paymentRow}>
                <span style={styles.paymentLabel}>Amount Paid</span>
                <span style={styles.paymentFee}>₹{inspection?.inspection_fee || 0}</span>
              </div>

              <div style={styles.divider}></div>

              {/* Payment Mode */}
              <div style={styles.paymentRow}>
                <span style={styles.paymentLabel}>Payment Mode</span>
                <span style={styles.gridValue}>Razorpay</span>
              </div>

              {/* Transaction ID */}
              {inspection?.razorpay_payment_id && (
                <div style={styles.paymentRow}>
                  <span style={styles.paymentLabel}>Transaction ID</span>
                  <span style={styles.transactionId}>{inspection.razorpay_payment_id}</span>
                </div>
              )}

              <div style={styles.divider}></div>

              {/* Payment Status */}
              <div style={styles.paymentStatusContainer}>
                <span style={styles.paymentLabel}>Payment Status</span>
                <div 
                  style={{
                    ...styles.paymentStatusBox,
                    backgroundColor: getPaymentStatusColor(inspection?.payment_status || 'success')
                  }}
                >
                  <span style={styles.paymentStatusText}>
                    {inspection?.payment_status === 'success' ? '✅' : '⏳'} {getPaymentStatusLabel(inspection?.payment_status || 'success')}
                  </span>
                </div>
              </div>

              {/* Payment Date */}
              {inspection?.created_at && (
                <>
                  <div style={styles.divider}></div>
                  <div style={styles.paymentRow}>
                    <span style={styles.paymentLabel}>Payment Date</span>
                    <span style={styles.gridValue}>{formatDate(inspection.created_at)}</span>
                  </div>
                </>
              )}

              {/* Success Message */}
              {inspection?.payment_status === 'success' && (
                <div style={styles.successMessage}>
                  <CheckCircle size={20} color="#4ECDC4" />
                  <span>Payment completed successfully. Your inspection is confirmed.</span>
                </div>
              )}

              {/* Failed Message */}
              {inspection?.payment_status === 'failed' && (
                <div style={styles.errorMessage}>
                  <AlertCircle size={20} color="#E74C3C" />
                  <span>Payment failed. Please contact support.</span>
                </div>
              )}
            </div>
          </section>

          {/* Next Steps */}
          <section style={styles.section}>
            <h3 style={styles.sectionTitle}>📝 What's Next?</h3>
            <div style={styles.stepsList}>
              <div style={styles.step}>
                <span style={styles.stepNumber}>1</span>
                <div style={styles.stepContent}>
                  <strong>Confirmation</strong>
                  <p>You'll receive SMS & email confirmation within 2 hours</p>
                </div>
              </div>
              <div style={styles.step}>
                <span style={styles.stepNumber}>2</span>
                <div style={styles.stepContent}>
                  <strong>Inspection</strong>
                  <p>Expert inspector will thoroughly check your car</p>
                </div>
              </div>
              <div style={styles.step}>
                <span style={styles.stepNumber}>3</span>
                <div style={styles.stepContent}>
                  <strong>Quote</strong>
                  <p>Receive fair market price immediately after inspection</p>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <button onClick={onClose} style={styles.closeButtonFooter}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px'
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '15px',
    boxShadow: '0 10px 50px rgba(0, 0, 0, 0.3)',
    maxWidth: '700px',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px',
    borderBottom: '2px solid #e0e0e0',
    backgroundColor: '#f9f9f9'
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#003566',
    margin: '0'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#999',
    transition: 'color 0.2s'
  },
  content: {
    padding: '24px'
  },
  section: {
    marginBottom: '24px',
    paddingBottom: '24px',
    borderBottom: '1px solid #e0e0e0'
  },
  sectionTitle: {
    fontSize: '1.1rem',
    fontWeight: '700',
    color: '#003566',
    marginBottom: '16px',
    marginTop: '0'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px'
  },
  gridItem: {
    display: 'flex',
    flexDirection: 'column'
  },
  gridLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#999',
    marginBottom: '6px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  gridValue: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#333'
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 'fit-content',
    padding: '4px 12px',
    color: '#fff',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600'
  },
  paymentSection: {
    marginBottom: '24px',
    paddingBottom: '24px',
    borderBottom: '1px solid #e0e0e0'
  },
  paymentSectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px'
  },
  paymentBox: {
    backgroundColor: '#f8fbff',
    border: '2px solid #e0e8f0',
    borderRadius: '12px',
    padding: '20px'
  },
  paymentRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px'
  },
  paymentLabel: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#666'
  },
  paymentFee: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#FFD60A'
  },
  transactionId: {
    fontSize: '12px',
    fontFamily: 'monospace',
    color: '#003566',
    backgroundColor: '#e8f0ff',
    padding: '4px 8px',
    borderRadius: '4px'
  },
  divider: {
    height: '1px',
    backgroundColor: '#e0e0e0',
    margin: '12px 0'
  },
  paymentStatusContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  paymentStatusBox: {
    padding: '12px 16px',
    borderRadius: '8px',
    textAlign: 'center'
  },
  paymentStatusText: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#fff'
  },
  successMessage: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginTop: '16px',
    padding: '12px 16px',
    backgroundColor: '#e8f5e9',
    border: '2px solid #4ECDC4',
    borderRadius: '8px',
    color: '#2e7d32',
    fontWeight: '500',
    fontSize: '14px'
  },
  errorMessage: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginTop: '16px',
    padding: '12px 16px',
    backgroundColor: '#ffebee',
    border: '2px solid #E74C3C',
    borderRadius: '8px',
    color: '#c62828',
    fontWeight: '500',
    fontSize: '14px'
  },
  stepsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  step: {
    display: 'flex',
    gap: '12px',
    padding: '12px',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px'
  },
  stepNumber: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '32px',
    height: '32px',
    backgroundColor: '#003566',
    color: '#fff',
    borderRadius: '50%',
    fontWeight: '700',
    fontSize: '14px'
  },
  stepContent: {
    display: 'flex',
    flexDirection: 'column'
  },
  footer: {
    padding: '16px 24px',
    borderTop: '1px solid #e0e0e0',
    backgroundColor: '#f9f9f9',
    display: 'flex',
    justifyContent: 'flex-end'
  },
  closeButtonFooter: {
    padding: '10px 24px',
    backgroundColor: '#003566',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  }
};

export default InspectionModal;